import { useState, useCallback } from "react";
import BillingDetailsCard from "./components/BillingDetailsCard";
import Button from "components/Button";
import Select, { SelectOption } from "components/Select";
import Spinner from "components/Spinner";
import PrevNextPagination from "components/PrevNextPagination";
import scraperApi from "api";
import Toaster from "components/Toaster";
import { useInvoicesPaginated, InvoiceStatusFilter } from "v2/hooks/billing/useInvoices";
import { useUser } from "routes/dataroutes/UserData";
import LoadingCard from "../components/LoadingCard";
import InvoiceRow from "./InvoiceRow";
import {
  trackBillingV2InvoiceFilterChanged,
  trackBillingV2InvoicePayNowClicked,
} from "utils/Tracking";


const STATUS_FILTER_OPTIONS: SelectOption<InvoiceStatusFilter>[] = [
  { name: "All",     value: "all" },
  { name: "Paid",    value: "paid" },
  { name: "Unpaid",  value: "unpaid" },
  { name: "Voided",  value: "voided" },
];


export default function PaymentHistoryCard() {
  const user = useUser();
  const {
    invoices,
    currentPage,
    hasNextPage,
    hasPrevPage,
    goToPage,
    statusFilter,
    setStatusFilter,
    isInitialLoad,
    isPaginating,
    error,
    retry,
    downloadInvoice,
  } = useInvoicesPaginated();

  const [payNowLoadingId, setPayNowLoadingId] = useState<string | null>(null);

  const handleFilterChange = (value: InvoiceStatusFilter) => {
    if (value === statusFilter) return;
    trackBillingV2InvoiceFilterChanged(value);
    setStatusFilter(value);
  };

  const handlePayNow = useCallback(async (invoiceId: string) => {
    trackBillingV2InvoicePayNowClicked({ invoice_id: invoiceId });
    setPayNowLoadingId(invoiceId);
    try {
      const result = await scraperApi.subscription.payNowLink();
      if (!result?.url) throw new Error("No payment URL");
      window.open(result.url, "_blank");
    } catch {
      Toaster.error(
        "Unable to generate payment link",
        <>Please try again or <a href="mailto:support@scraperapi.com" className="underline">contact support</a> for assistance.</>
      );
    } finally {
      setPayNowLoadingId(null);
    }
  }, []);


  const selectedFilterOption = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)
    || STATUS_FILTER_OPTIONS[0];

  return (
    <LoadingCard isLoading={isInitialLoad}>
      <BillingDetailsCard
        id="payment-history"
        title="Payment History"
        subtitle="View and manage your invoices"
        className="!overflow-visible"
        extra={
          <Select
            value={ selectedFilterOption }
            options={ STATUS_FILTER_OPTIONS }
            onChange={ option => handleFilterChange(option.value) }
            size="SM"
            className="w-32"
          />
        }
      >
        <div className="flex flex-col gap-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50">
              <tr>
                <th className="w-8" /* Expand chevron... must be an inline comment, else React would throw rendering errors because of unexpected whitespaces in <tr> */ />
                <th className="text-left h-[48px] px-4 text-sm font-semibold text-neutral-900">
                  Date
                </th>
                <th className="text-left h-[48px] px-4 text-sm font-semibold text-neutral-900">
                  Plan
                </th>
                <th className="text-right h-[48px] px-4 text-sm font-semibold text-neutral-900">
                  Amount
                </th>
                <th className="text-left h-[48px] px-4 text-sm font-semibold text-neutral-900">
                  Status
                </th>
                <th className="text-left h-[48px] px-4 text-sm font-semibold text-neutral-900">
                  Invoice
                </th>
                <th className="text-right h-[48px] px-4 text-sm font-semibold text-neutral-900">
                  Action
                </th>
              </tr>
              </thead>

              {/* Error state */}
              {error && !isPaginating ? (
                <tbody>
                <tr>
                  <td colSpan={7} className="py-8">
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-sm text-gray-600">
                        Failed to load invoices. Please try again.
                      </p>
                      <Button
                        text="RETRY"
                        onClick={retry}
                        className="button button-primary w-fit"
                        size="SM"
                      />
                    </div>
                  </td>
                </tr>
                </tbody>

              ) : invoices.length > 0 ? (
                <>
                  {invoices.map((invoice) => (
                    <InvoiceRow
                      key={invoice.id}
                      invoice={invoice}
                      onDownload={downloadInvoice}
                      onPayNow={handlePayNow}
                      payNowLoading={payNowLoadingId === invoice.id}
                      payNowDisabled={payNowLoadingId !== null}
                      showPayNow={(invoice.status === "not_paid" || invoice.status === "payment_due" || invoice.status === "posted") && user?.hasPaymentIssues}
                      actionsDisabled={isPaginating}
                    />
                  ))}
                </>

              ) : !isPaginating ? (
                <tbody>
                <tr>
                  <td colSpan={7} className="py-8 text-center text-sm text-gray-600">
                    {statusFilter !== "all"
                      ? "No invoices match this filter"
                      : "No payment history available"}
                  </td>
                </tr>
                </tbody>
              ) : null}
            </table>
          </div>

          {/* Pagination or loading spinner */}
          {isPaginating ? (
            <div className="flex justify-center items-center mt-4">
              <Spinner size="SM" theme="secondary" />
            </div>
          ) : (hasNextPage || hasPrevPage) && (
            <PrevNextPagination
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              hasPrevPage={hasPrevPage}
              onPageChange={goToPage}
            />
          )}
        </div>
      </BillingDetailsCard>
    </LoadingCard>
  );
}
